import {
  CustomReport,
  CustomReportExhibitor,
  CustomReportFileLink,
  CustomReportTasks,
  CustomReportTasksColumnsType,
  Field,
  Sheet,
} from "ts/api/customReport";
import { downloadFromWorkbook } from "tools/file/downloadFileFromCsvData";
import { cloneDeep, orderBy, sortBy } from "lodash";
import { ExhibitorPairStatus } from "@evennt-tech/api-v2-core/dist/exhibitor";
import showParentTaskDataTypes from "./useShowCustomReportsExhibitorList/constants/showParentTaskDataTypes";
import columnsTypeLabelMap from "./useShowCustomReportsExhibitorList/constants/columnsTypeLabelMap";
import { getStandSizeValue } from "../Show/Exhibitor/Settings/components/SizeSettingRow";

type FieldData = {
  [key: string]:
    | string
    | ((exhibitor: CustomReportExhibitor, showId?: string) => string | number);
};

type RichTextCellRef = {
  sheetIndex: number;
  rowIndex: number;
  columnIndex: number;
  files: CustomReportFileLink[];
};

export const fieldPath: FieldData = {
  "Company Name": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company.name;
  },
  "Company Name Suffix": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company.suffixName;
  },
  "Company Logo": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.company?.companyInfo?.logoFile?.url || "";
  },
  "Registered Address": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company.companyInfo?.address || "";
  },
  Postcode: (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company?.companyInfo?.postcode || "";
  },
  "Web Address": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company?.companyInfo?.webAddress || "";
  },
  "Company ID": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.company.id;
  },
  "Primary Contact Invitation Link": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.primaryConnection?.inviteLink;
  },
  "Invitation Link": (exhibitor: CustomReportExhibitor, showId?: string) => {
    return exhibitor.userConnection?.inviteLink;
  },
  "Build Type": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.builder?.name || "";
  },
  Packages: (exhibitor: CustomReportExhibitor) => {
    return sortBy(
      exhibitor.newStand?.standToPackages || [],
      (standToPackage) => standToPackage?.package?.name
    )
      .map(
        ({ quantity, package: exhibitorPackage }) =>
          `${exhibitorPackage?.name} [${quantity}]`
      )
      .join(", ");
  },
  "Stand Number": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.name || "";
  },
  "Stand Number Suffix": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.suffixName || "";
  },
  "Stand ID": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand.standId || "";
  },
  Size: (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.newStand?.size?.length &&
      exhibitor?.newStand?.size?.width &&
      exhibitor?.showUnitSystem
      ? getStandSizeValue(exhibitor?.newStand?.size, exhibitor?.showUnitSystem)
      : "";
  },
  Width: (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.size?.width || "";
  },
  Height: (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.size?.length || "";
  },
  Area: (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.size?.area || "";
  },
  "Open sides": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.openSides || "";
  },
  "Wall Length": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.newStand?.standWallLength || "";
  },
  "Email Address": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.userConnection?.exhibitorUser?.user?.email || "";
  },
  "User ID": (exhibitor: CustomReportExhibitor) => {
    return exhibitor.userConnection.exhibitorUser?.userId || "";
  },
  Status: (exhibitor: CustomReportExhibitor) => {
    return exhibitor.status || "";
  },
  "Primary Contact First Name": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.primaryConnection.exhibitorUser?.name.split(" ")[0] || "";
  },
  "Primary Contact Last Name": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.primaryConnection.exhibitorUser?.name.split(" ")[1] || "";
  },
  "Primary Contact Telephone": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.exhibitorPair?.contactPhoneNumber || "";
  },
  "Primary Contact Email": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.primaryConnection.exhibitorUser?.user?.email || "";
  },
  "Primary Contact User ID": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.primaryConnection.exhibitorUser.userId || "";
  },
  "Primary Contact Status": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.exhibitorPair.status === ExhibitorPairStatus.ARCHIVED
      ? ExhibitorPairStatus.ARCHIVED
      : exhibitor?.primaryConnection.status;
  },
  "First Name": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.userConnection.exhibitorUser?.name.split(" ")[0] || "";
  },
  "Last Name": (exhibitor: CustomReportExhibitor) => {
    return exhibitor?.userConnection.exhibitorUser?.name.split(" ")[1] || "";
  },
};

export const generateXLSXFields = (
  report: CustomReport,
  exhibitors: any,
  filterData: any,
  showId: string
): { sheetData: string[][][]; richTextCells: RichTextCellRef[] } => {
  const sheetData: string[][][] = [];
  const richTextCells: RichTextCellRef[] = [];

  report.sheets.forEach((sheet: Sheet, index: number) => {
    if (!exhibitors?.[index]?.data) return;

    let updatedData = cloneDeep(exhibitors[index].data);
    if (filterData[index]) {
      const { sort, filters, search } = filterData[index];
      if (filters) {
        filters.forEach((filter: any) => {
          updatedData = updatedData.filter((row: any) =>
            filter.value.includes(row[filter.fieldPath])
          );
        });
      }
      if (sort) {
        updatedData = orderBy(
          updatedData,
          [sort.key],
          [sort.direction || "asc"]
        );
      }
    }

    let data: string[][] = [
      sheet.columnsOrder.flatMap((column: Field) => {
        if (
          column.type === "customFields" &&
          exhibitors[index].data.length > 0
        ) {
          const { customFields } = exhibitors[index].data[0];
          if (customFields) {
            const customKeys =
              customFields && Object.keys(customFields.columns);

            const key =
              customKeys?.find((key: any) => {
                return customFields?.columns[key].id === column.columnId;
              }) || -1;
            const ind = Number(key);

            if (ind === -1) {
              return [];
            }

            if (ind >= 0 && customFields) {
              return customFields?.columns[ind].value;
            }
          }
        }
        if (column.type === "tasks" && exhibitors[index].data.length > 0) {
          const { tasks } = exhibitors[index].data[0] as CustomReportExhibitor;

          // Match both task id schemes.
          const currentColumns = tasks?.columns?.filter(
            (taskItem) =>
              taskItem.id === column?.columnId ||
              taskItem.taskUuid === column?.columnId
          );

          const showOnlyParentTaskDataForMultitask =
            currentColumns?.[0]?.type ===
              CustomReportTasksColumnsType.multitask &&
            column?.taskType &&
            showParentTaskDataTypes.includes(column?.taskType);

          if (!currentColumns?.length) {
            return [];
          }
          const columnLabel = column.taskType
            ? columnsTypeLabelMap[column.taskType]
            : column.taskType;
          if (
            currentColumns?.[0]?.type === CustomReportTasksColumnsType.task ||
            showOnlyParentTaskDataForMultitask
          ) {
            if (columnLabel === columnsTypeLabelMap.response) {
              return currentColumns.map(
                (currentColumn) => `${currentColumn.value} (${columnLabel})`
              );
            }
            return `${currentColumns?.[0]?.value} (${columnLabel})`;
          }

          if (
            currentColumns?.[0]?.type === CustomReportTasksColumnsType.multitask
          ) {
            const subtasks = currentColumns?.[0]?.subtasks;

            return subtasks?.map((subtask) => {
              return `${subtask.value} (${columnLabel})`;
            });
          }
        }
        return column.label;
      }),
    ];

    updatedData.forEach((exhibitor: CustomReportExhibitor) => {
      let row: string[] = [];
      sheet.columnsOrder.forEach((column: Field) => {
        if (
          column.type === "customFields" &&
          exhibitors[index].data.length > 0
        ) {
          const { customFields } = exhibitor;
          if (customFields) {
            const customKeys =
              customFields && Object.keys(customFields.columns);
            const key =
              customKeys?.find((key: any) => {
                return customFields?.columns[key].id === column.columnId;
              }) || -1;
            const ind = Number(key);
            if (ind >= 0) {
              const answer = exhibitor?.customFields?.answers[ind];
              if (answer?.fileLinks?.length) {
                richTextCells.push({
                  sheetIndex: index,
                  rowIndex: data.length,
                  columnIndex: row.length,
                  files: answer.fileLinks,
                });
              }
              row.push(answer?.value || "N/A");
            }
          }
        } else if (column.type === "tasks") {
          const { tasks } = exhibitor;
          const matchesColumnId = (taskItem: {
            id: string;
            taskUuid?: string;
          }) =>
            taskItem.id === column?.columnId ||
            taskItem.taskUuid === column?.columnId;
          const currentColumns = tasks?.columns?.filter(matchesColumnId);

          const taskValues =
            column?.taskType && tasks
              ? tasks[column.taskType as keyof CustomReportTasks].filter(
                  matchesColumnId
                )
              : undefined;

          const naString = "N/A";
          const showOnlyParentTaskDataForMultitask =
            currentColumns?.[0]?.type ===
              CustomReportTasksColumnsType.multitask &&
            column?.taskType &&
            showParentTaskDataTypes.includes(column?.taskType);

          if (
            currentColumns?.[0]?.type === CustomReportTasksColumnsType.task ||
            showOnlyParentTaskDataForMultitask
          ) {
            const columnLabel = column.taskType
              ? columnsTypeLabelMap[column.taskType]
              : column.taskType;
            if (columnLabel === columnsTypeLabelMap.response) {
              taskValues?.forEach((taskValue) => {
                if (taskValue?.fileLinks?.length) {
                  richTextCells.push({
                    sheetIndex: index,
                    rowIndex: data.length,
                    columnIndex: row.length,
                    files: taskValue.fileLinks,
                  });
                }
                row.push(taskValue?.value || naString);
              });
            } else {
              row.push(taskValues?.[0]?.value || naString);
            }
            return;
          }
          if (
            currentColumns?.[0]?.type === CustomReportTasksColumnsType.multitask
          ) {
            const subtaskValues = taskValues?.[0]?.subtasks;
            return subtaskValues?.forEach((subtask) => {
              if (subtask?.fileLinks?.length) {
                richTextCells.push({
                  sheetIndex: index,
                  rowIndex: data.length,
                  columnIndex: row.length,
                  files: subtask.fileLinks,
                });
              }
              row.push(subtask.value || naString);
            });
          }
        } else if (typeof fieldPath[column.label] === "function") {
          row.push(
            (fieldPath[column.label] as (
              exhibitor: CustomReportExhibitor,
              showId?: string
            ) => string)(exhibitor, showId) || "N/A"
          );
        } else {
          row.push("N/A");
        }
      });
      data.push(row);
    });
    sheetData.push(data);
  });

  return { sheetData, richTextCells };
};

export const buildXLSXWorkbook = async (
  report: CustomReport,
  exhibitors: any,
  filterData: any,
  showId: string
) => {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  if (report.sheets) {
    const { sheetData, richTextCells } = generateXLSXFields(
      report,
      exhibitors,
      filterData,
      showId
    );

    sheetData.forEach((data, sheetIndex) => {
      const worksheet = workbook.addWorksheet(
        report.sheets[sheetIndex].sheetTitle
      );

      const cellsByRow = new Map<number, Map<number, CustomReportFileLink[]>>();
      richTextCells
        .filter((cell) => cell.sheetIndex === sheetIndex)
        .forEach((cell) => {
          const byColumn = cellsByRow.get(cell.rowIndex) ?? new Map();
          byColumn.set(cell.columnIndex, cell.files);
          cellsByRow.set(cell.rowIndex, byColumn);
        });

      data.forEach((row, rowIndex) => {
        const byColumn = cellsByRow.get(rowIndex);
        const maxFiles = byColumn
          ? Math.max(1, ...Array.from(byColumn.values()).map((f) => f.length))
          : 1;

        if (maxFiles <= 1) {
          const addedRow = worksheet.addRow(row);
          byColumn?.forEach((files, columnIndex) => {
            const file = files[0];
            if (!file) return;
            addedRow.getCell(columnIndex + 1).value = {
              text: file.name,
              hyperlink: file.url,
            };
          });
          return;
        }

        for (let sub = 0; sub < maxFiles; sub += 1) {
          const addedRow = worksheet.addRow(
            sub === 0 ? row : row.map(() => "")
          );
          byColumn?.forEach((files, columnIndex) => {
            const file = files[sub];
            const cell = addedRow.getCell(columnIndex + 1);
            cell.value = file ? { text: file.name, hyperlink: file.url } : "";
          });
        }
      });

      const numColumns = data[0]?.length ?? 0;
      for (let col = 1; col <= numColumns; col += 1) {
        const column = worksheet.getColumn(col);
        let maxLength = 10;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value as unknown;
          const cellText =
            cellValue && typeof cellValue === "object" && "text" in cellValue
              ? String((cellValue as { text: unknown }).text)
              : String(cellValue ?? "");
          maxLength = Math.max(maxLength, cellText.length);
        });
        column.width = Math.min(maxLength + 4, 60);
        column.alignment = {
          vertical: "top",
          horizontal: "left",
          wrapText: true,
        };
      }
    });
  }

  return workbook;
};

export const generateXLSXFile = async (
  report: CustomReport,
  exhibitors: any,
  filterData: any,
  showId: string
) => {
  const workbook = await buildXLSXWorkbook(
    report,
    exhibitors,
    filterData,
    showId
  );
  downloadFromWorkbook(workbook, `${report.title}`);
};
